import { Controller } from '@hotwired/stimulus'

const MOBILE_BREAKPOINT = 768
const NEAR_BOTTOM_THRESHOLD = 100
const RETRY_DELAYS_MS = [0, 50, 150, 300, 600, 1000, 2000]

export default class extends Controller {
    static targets = ['messages']

    connect() {
        this.initialScrollDone = false
        this.scrollTimeoutIds = []
        this.updateViewportHeight()
        this.scheduleScrollPasses()

        this.onResize = () => {
            this.updateViewportHeight()
            if (this.isNearBottom()) {
                this.scrollToBottom()
            }
        }
        window.addEventListener('resize', this.onResize)

        if (document.readyState !== 'complete') {
            this.onWindowLoad = () => this.scrollToBottom()
            window.addEventListener('load', this.onWindowLoad, { once: true })
        }

        if (this.hasMessagesTarget) {
            this.observer = new MutationObserver(() => {
                if (this.isNearBottom()) {
                    this.scrollToBottom()
                }
            })
            this.observer.observe(this.messagesTarget, { childList: true, subtree: true })

            this.messagesTarget.querySelectorAll('img').forEach(img => {
                if (!img.complete) {
                    img.addEventListener('load', () => {
                        if (this.isNearBottom()) {
                            this.scrollToBottom()
                        }
                    }, { once: true })
                    img.addEventListener('error', () => {
                        if (this.isNearBottom()) {
                            this.scrollToBottom()
                        }
                    }, { once: true })
                }
            })
            this.onLocalNewMessage = event => {
                if (event.detail?.local) {
                    requestAnimationFrame(() => this.scrollToBottom())
                }
            }
            this.messagesTarget.addEventListener('litigant-messages:new-message', this.onLocalNewMessage)
        }
    }

    disconnect() {
        if (this.scrollTimeoutIds) {
            this.scrollTimeoutIds.forEach(id => clearTimeout(id))
            this.scrollTimeoutIds = []
        }
        if (this.onResize) {
            window.removeEventListener('resize', this.onResize)
        }
        if (this.onWindowLoad) {
            window.removeEventListener('load', this.onWindowLoad)
        }
        if (this.observer) {
            this.observer.disconnect()
        }
        if (this.onLocalNewMessage && this.hasMessagesTarget) {
            this.messagesTarget.removeEventListener('litigant-messages:new-message', this.onLocalNewMessage)
        }
    }

    updateViewportHeight() {
        const vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--litigant-chat-vh', `${vh}px`)
    }

    isNearBottom() {
        if (!this.initialScrollDone) {
            return true
        }
        if (!this.hasMessagesTarget) {
            return true
        }
        const element = this.messagesTarget
        return (element.scrollHeight - element.scrollTop - element.clientHeight) < NEAR_BOTTOM_THRESHOLD
    }

    scheduleScrollPasses() {
        RETRY_DELAYS_MS.forEach(delay => {
            const id = setTimeout(() => this.scrollToBottom(), delay)
            this.scrollTimeoutIds.push(id)
        })
    }

    scrollToBottom() {
        if (!this.hasMessagesTarget) {
            return
        }
        const element = this.messagesTarget
        const lastChild = element.lastElementChild

        void element.offsetHeight

        if (lastChild && typeof lastChild.scrollIntoView === 'function') {
            try {
                lastChild.scrollIntoView({ block: 'end', inline: 'nearest' })
            } catch (e) {
                element.scrollTop = element.scrollHeight
            }
        } else {
            element.scrollTop = element.scrollHeight
        }

        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            window.scrollTo(0, document.body.scrollHeight)
        }

        this.initialScrollDone = true
    }
}
